import {
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Color,
  DoubleSide,
  Group,
  Line,
  LineBasicMaterial,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  Quaternion,
  Raycaster,
  SphereGeometry,
  SRGBColorSpace,
  Vector2,
  Vector3,
  type Intersection,
  type PerspectiveCamera,
} from "three";
import { Ioc } from "@core/ioc/ioc";
import {
  worldToStagePosition,
  worldToStageQuaternion,
} from "@runtime/xr/stageSpace";
import { FRAME_MODEL, type FrameModel } from "@core/frame/frame";
import {
  annotationAuthorColor,
  type AnnotationData,
  type AnnotationVec3,
} from "@models/data/annotationData";
import type { IAnnotationsModel } from "@models/annotations/IAnnotationsModel";
import type { ISketchModel } from "@models/annotations/ISketchModel";
import type { IProjectModel } from "@models/project/IProjectModel";
import { convertBasisPosition } from "@models/utils/unityBasis";
import { ORBIT_CAMERA, type OrbitCameraController } from "@runtime/camera/orbitCameraController";
import { PICKING_SERVICE, type PickingService } from "@runtime/picking/pickingService";
import type { ThreeEngine } from "@runtime/engine/engineService";
import {
  bubbleContentKey,
  composeAnnotationBubble,
  loadAnnotationBubbleFonts,
} from "./annotationBubbleCanvas";

/**
 * `AnnotationsRenderer` — draws scene-placed comment markers in the viewport.
 *
 * Unity reference: `Content/Gizmos/ActionAnnotationPrefab.prefab` +
 * `Content/Controls/AnnotationVisual.prefab` + `Runtime/Gizmos/AnnotationBehaviour.cs`.
 * Each anchored annotation renders as:
 *   - a **billboard bubble** (white rounded card + black text canvas texture)
 *     that faces the camera (`quaternion = camera.quaternion`, Unity desktop
 *     `Billboard`) and holds a **constant on-screen size** (scale ∝ distance,
 *     Unity `ConstantSizeForCamera`);
 *   - a small translucent **anchor sphere** on the surface (fixed world size);
 *   - a **cubic bezier line** (40 samples) from the anchor to the bubble,
 *     rendered as a thin white line (`BezierCurve.cs`).
 *
 * Only annotations that carry a `placementPosition` render (scene-placed via
 * the Comment tool); detached list-only comments don't. Positions are Unity
 * world space on the model and convert at this boundary the same way media
 * tiles / avatars do — the canonical `M = diag(-1,1,1)`, via
 * `@models/utils/unityBasis`.
 *
 * Web-port deviations (documented in AnnotationData): world-space anchoring
 * (not Unity part-local), a projection-consistent constant-screen-size scale
 * (equivalent to Unity's distance-linear heuristic), and a plain 1px `Line`
 * for the bezier (WebGL renders line width at 1px, matching the prefab's
 * 1px-screen Shapes polyline). Web-only: the anchor dot is a drag handle too
 * ({@link hitTestHandle}, {@link setActiveAnchor}) — Unity never moves an
 * anchor once placed.
 */
export class AnnotationsRenderer {
  private readonly m_root = new Group();
  private readonly m_visuals = new Map<string, AnnotationVisual>();
  private readonly m_offFns: Array<() => void> = [];
  private m_activeSceneId: string | null = null;
  private m_editingId: string | null = null;
  private m_activeAnchorId: string | null = null;
  private m_disposed = false;

  constructor(
    private readonly m_engine: ThreeEngine,
    private readonly m_project: IProjectModel,
    private readonly m_annotations: IAnnotationsModel,
    /** Optional so the renderer keeps working before sketch wiring exists. */
    private readonly m_sketches: ISketchModel | null = null,
  ) {
    this.m_root.name = "campfire-annotations";
  }

  mount(): void {
    if (this.m_disposed) return;
    this.m_engine.root.add(this.m_root);
    this.m_offFns.push(
      // Drawn after ambient occlusion and the outlines, so neither shades a
      // marker that sits on top of the geometry.
      this.m_engine.addScreenOverlay(this.m_root),
      this.m_project.onActiveSceneChanged.on(() => this.rebuild()),
      this.m_annotations.onChanged.on(({ sceneId }) => {
        if (sceneId === this.m_project.activeSceneId) this.rebuild();
      }),
      this.m_annotations.onAnnotationMoved.on(({ sceneId, annotationId }) => {
        if (sceneId === this.m_project.activeSceneId) this.moveVisual(annotationId);
      }),
      this.m_annotations.onVisibilityChanged.on((visible) => {
        this.m_root.visible = visible;
        this.m_engine.requestRender();
      }),
      this.m_project.onActiveProjectChanged.on((project) => {
        if (!project) this.clearVisuals();
      }),
      Ioc.resolve<FrameModel>(FRAME_MODEL).onTick.on(() => this.tick()),
    );
    if (this.m_sketches) {
      // A sketch's PNGs changed (local save or remote `sketch_uploaded`) —
      // force the preview face to recompose on the next rebuild.
      const invalidate = ({ sketchId }: { sketchId: string }) => {
        const visual = this.m_visuals.get(sketchId);
        if (visual) visual.contentKey = "";
        this.rebuild();
      };
      this.m_offFns.push(
        this.m_sketches.onSketchUploaded.on(invalidate),
        this.m_sketches.onScreenshotUpdated.on(invalidate),
      );
    }
    this.m_root.visible = this.m_annotations.areAnnotationsVisible;
    this.rebuild();
    // Faces composed before Greycliff arrived used the fallback font.
    void loadAnnotationBubbleFonts().then(() => {
      if (this.m_disposed) return;
      for (const visual of this.m_visuals.values()) visual.contentKey = "";
      this.rebuild();
    });
  }

  dispose(): void {
    this.m_disposed = true;
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
    this.clearVisuals();
    this.m_root.removeFromParent();
  }

  /**
   * Hide the 3D bubble for the annotation currently being text-edited (the
   * inline HTML editor takes its place, so the two read as one element). The
   * anchor dot + connector line stay visible. Pass `null` to restore.
   */
  setEditing(annotationId: string | null): void {
    this.m_editingId = annotationId;
    const previous = this.m_visuals.get(this.m_editingId ?? "");
    if (previous) previous.bubbleMesh.visible = false;
    // A no-longer-edited bubble is re-shown in the next tick.
    this.m_engine.requestRender();
  }

  /**
   * The marker handle under canvas point (`x`, `y`) — what the comment drag in
   * `AnnotationToolController` grabs. A bubble wins, and is raycast on its own
   * meshes rather than through a scene pick: it draws over everything, so a
   * part standing in front of it must not steal the press. Otherwise the
   * nearest anchor dot within {@link ANCHOR_HIT_PX}, measured on screen — the
   * dot is a pixel or two across at working distance, too small to raycast.
   */
  hitTestHandle(x: number, y: number): AnnotationHandleHit | null {
    if (this.m_disposed || !this.m_root.visible || this.m_visuals.size === 0) return null;
    const camera = this.camera();
    if (!camera) return null;
    const width = Math.max(1, this.m_engine.canvas.clientWidth);
    const height = Math.max(1, this.m_engine.canvas.clientHeight);

    SCRATCH_NDC.set((x / width) * 2 - 1, -(y / height) * 2 + 1);
    SCRATCH_RAYCASTER.setFromCamera(SCRATCH_NDC, camera);
    SCRATCH_HITS.length = 0;
    for (const visual of this.m_visuals.values()) {
      // Hidden while its inline editor stands in for it.
      if (!visual.bubbleMesh.visible) continue;
      visual.bubbleMesh.updateWorldMatrix(true, false);
      // Appends and re-sorts, so the nearest bubble — the one drawn on top —
      // ends up first.
      SCRATCH_RAYCASTER.intersectObject(visual.bubbleMesh, false, SCRATCH_HITS);
    }
    const bubbleHit = SCRATCH_HITS[0]?.object;
    SCRATCH_HITS.length = 0;
    if (bubbleHit) {
      for (const visual of this.m_visuals.values()) {
        if (visual.bubbleMesh !== bubbleHit) continue;
        const screen = this.projectToCanvas(visual, visual.markerWorld, camera, width, height);
        return {
          annotationId: visual.id,
          handle: "bubble",
          screenX: screen?.x ?? x,
          screenY: screen?.y ?? y,
        };
      }
    }

    let best: AnnotationHandleHit | null = null;
    let bestDistance = ANCHOR_HIT_PX;
    for (const visual of this.m_visuals.values()) {
      const screen = this.projectToCanvas(visual, visual.anchorWorld, camera, width, height);
      if (!screen) continue;
      const distance = Math.hypot(screen.x - x, screen.y - y);
      if (distance > bestDistance) continue;
      bestDistance = distance;
      best = { annotationId: visual.id, handle: "anchor", screenX: screen.x, screenY: screen.y };
    }
    return best;
  }

  /**
   * Grow one anchor dot to at least {@link ANCHOR_ACTIVE_PX} on screen while
   * the pointer is over it or dragging it, so the user can see what they are
   * about to grab. `null` restores every dot.
   */
  setActiveAnchor(annotationId: string | null): void {
    if (this.m_disposed || this.m_activeAnchorId === annotationId) return;
    const previous = this.m_visuals.get(this.m_activeAnchorId ?? "");
    if (previous) previous.anchorMesh.scale.setScalar(1);
    this.m_activeAnchorId = annotationId;
    this.m_engine.requestRender();
  }

  private camera(): PerspectiveCamera | null {
    return Ioc.isRegistered<OrbitCameraController>(ORBIT_CAMERA)
      ? Ioc.resolve<OrbitCameraController>(ORBIT_CAMERA).camera
      : null;
  }

  /** Canvas px of a marker-local point, or null when it is behind the camera. */
  private projectToCanvas(
    visual: AnnotationVisual,
    point: Vector3,
    camera: PerspectiveCamera,
    width: number,
    height: number,
  ): { x: number; y: number } | null {
    // Marker points are stage-local (children of `engine.root`), so lift them
    // out of the stage before projecting — see `runtime/xr/stageSpace.ts`.
    visual.group.updateWorldMatrix(true, false);
    const ndc = visual.group.localToWorld(SCRATCH_PROJECTED.copy(point)).project(camera);
    if (ndc.z > 1) return null;
    return { x: (ndc.x * 0.5 + 0.5) * width, y: (-ndc.y * 0.5 + 0.5) * height };
  }

  private clearVisuals(): void {
    this.m_engine.requestRender();
    for (const visual of this.m_visuals.values()) this.disposeVisual(visual);
    this.m_visuals.clear();
    this.m_activeSceneId = null;
  }

  private disposeVisual(visual: AnnotationVisual): void {
    visual.group.removeFromParent();
    visual.bubbleMesh.geometry.dispose();
    visual.bubbleMaterial.map?.dispose();
    visual.bubbleMaterial.dispose();
    visual.anchorMesh.geometry.dispose();
    visual.anchorMaterial.dispose();
    visual.line.geometry.dispose();
    visual.lineMaterial.dispose();
  }

  private rebuild(): void {
    if (this.m_disposed) return;
    this.m_engine.requestRender();
    const sceneId = this.m_project.activeSceneId;
    this.m_activeSceneId = sceneId;

    const placed = sceneId ? this.placedAnnotations(sceneId) : [];
    const wanted = new Set(placed.map((a) => a.id));
    for (const [id, visual] of this.m_visuals) {
      if (wanted.has(id)) continue;
      this.disposeVisual(visual);
      this.m_visuals.delete(id);
    }
    for (const annotation of placed) this.placeVisual(annotation);
  }

  private placedAnnotations(sceneId: string): ReadonlyArray<AnnotationData> {
    return this.m_annotations
      .getAnnotations(sceneId)
      .filter(
        (a) =>
          a.placementPosition != null
          && !a.isResolved
          // Unity `AnnotationsLayerModel.IsValid`: a sketch counts only once
          // its PNGs finished uploading (`SketchUploaded == true`).
          && (a.type !== "sketch" || a.sketchUploaded !== false),
      );
  }

  private placeVisual(annotation: AnnotationData): void {
    let visual = this.m_visuals.get(annotation.id);
    if (!visual) {
      // Comment markers draw ON TOP of the scene (depthTest off + high
      // renderOrder) so the connector line + bubble are never occluded by
      // geometry between the anchor and the camera — the standard
      // always-visible comment-pin behavior. renderOrder within a marker:
      // line < dot < bubble so the bubble reads over its own connector.
      const bubbleMaterial = new MeshBasicMaterial({
        transparent: true,
        side: DoubleSide,
        depthTest: false,
      });
      bubbleMaterial.toneMapped = false;
      const bubbleMesh = new Mesh(new PlaneGeometry(1, 1), bubbleMaterial);
      bubbleMesh.name = `annotation-bubble:${annotation.id}`;
      bubbleMesh.renderOrder = 1002;

      const anchorMaterial = new MeshBasicMaterial({
        color: new Color(1, 1, 1),
        depthTest: false,
      });
      anchorMaterial.toneMapped = false;
      const anchorMesh = new Mesh(new SphereGeometry(ANCHOR_RADIUS_M, 12, 8), anchorMaterial);
      anchorMesh.renderOrder = 1001;

      const lineMaterial = new LineBasicMaterial({
        color: new Color(1, 1, 1),
        transparent: true,
        opacity: LINE_OPACITY,
        depthWrite: false,
        depthTest: false,
      });
      const lineGeometry = new BufferGeometry();
      lineGeometry.setAttribute(
        "position",
        new BufferAttribute(new Float32Array(BEZIER_SAMPLES * 3), 3),
      );
      const line = new Line(lineGeometry, lineMaterial);
      line.renderOrder = 1000;

      const group = new Group();
      group.name = `annotation:${annotation.id}`;
      group.add(line, anchorMesh, bubbleMesh);
      this.m_root.add(group);

      // The line + anchor dot are decoration — never intercept scene picks.
      // The bubble stays pickable (tagged below) so clicking it selects the
      // annotation and starts a drag.
      if (Ioc.isRegistered<PickingService>(PICKING_SERVICE)) {
        const picker = Ioc.resolve<PickingService>(PICKING_SERVICE);
        picker.markUnpickable(line);
        picker.markUnpickable(anchorMesh);
      }

      visual = {
        id: annotation.id,
        group,
        bubbleMesh,
        bubbleMaterial,
        anchorMesh,
        anchorMaterial,
        line,
        lineMaterial,
        contentKey: "",
        heightPx: 1,
        bottomMarginPx: 0,
        scale: 1,
        markerWorld: new Vector3(),
        anchorWorld: new Vector3(),
      };
      this.m_visuals.set(annotation.id, visual);
    }

    if (annotation.type === "sketch") {
      // Sketch pin: the billboard face is the blended PREVIEW image (Unity
      // `AnnotationBehaviour.SketchPreviewTexture`), framed like a card.
      const key = `sketch|${annotation.id}|${annotation.modifiedAt}`;
      if (visual.contentKey !== key) {
        visual.contentKey = key;
        void this.composeSketchFace(visual, annotation.id, key);
      }
    } else {
      // Recompose the face only when its inputs changed (text/author).
      const initials = annotation.creator.initials;
      const key = bubbleContentKey(annotation.text, false, initials);
      if (visual.contentKey !== key) {
        const composed = composeAnnotationBubble(annotation.text, false, {
          initials,
          color: annotationAuthorColor(annotation.creator.id),
        });
        this.applyFace(visual, composed.canvas, composed.widthPx, composed.heightPx, composed.bottomMarginPx);
        visual.contentKey = key;
      }
    }

    this.tagVisual(visual);
    this.updateVisualTransform(visual, annotation);
  }

  /** Swap the billboard's canvas face + plane geometry in one place. */
  private applyFace(
    visual: AnnotationVisual,
    canvas: HTMLCanvasElement,
    widthPx: number,
    heightPx: number,
    bottomMarginPx: number,
  ): void {
    // Render on demand: every face lands here, the async sketch ones included.
    this.m_engine.requestRender();
    visual.bubbleMaterial.map?.dispose();
    const texture = new CanvasTexture(canvas);
    texture.colorSpace = SRGBColorSpace;
    texture.minFilter = LinearFilter;
    visual.bubbleMaterial.map = texture;
    visual.bubbleMaterial.needsUpdate = true;
    visual.heightPx = heightPx;
    visual.bottomMarginPx = bottomMarginPx;
    visual.bubbleMesh.geometry.dispose();
    // Base plane in canvas px units; the per-tick uniform scale (∝ distance)
    // holds the on-screen size while content drives width + height.
    visual.bubbleMesh.geometry = new PlaneGeometry(widthPx, heightPx);
  }

  /** Load the sketch preview and draw it as a white-framed rounded card. */
  private async composeSketchFace(
    visual: AnnotationVisual,
    sketchId: string,
    key: string,
  ): Promise<void> {
    const url = this.m_sketches
      ? (await this.m_sketches.loadSketchPreviewTexture(sketchId))
        ?? (await this.m_sketches.loadBackgroundTexture(sketchId))
        ?? (await this.m_sketches.loadSketchTexture(sketchId))
      : null;
    // Stale guard: the visual may be gone or superseded after the awaits.
    if (this.m_disposed || this.m_visuals.get(sketchId) !== visual || visual.contentKey !== key) {
      return;
    }
    const image = url ? await loadImage(url) : null;
    if (this.m_disposed || this.m_visuals.get(sketchId) !== visual || visual.contentKey !== key) {
      return;
    }
    if (!image) {
      // No layers yet (upload race) — placeholder card with a label.
      const composed = composeAnnotationBubble("Sketch", false, {
        initials: "S",
        color: "#009ddb",
      });
      this.applyFace(visual, composed.canvas, composed.widthPx, composed.heightPx, composed.bottomMarginPx);
      return;
    }
    const frame = SKETCH_CARD_FRAME_PX;
    const width = SKETCH_CARD_WIDTH_PX;
    const height = Math.max(
      1,
      Math.round((image.naturalHeight / Math.max(1, image.naturalWidth)) * width),
    );
    const canvas = document.createElement("canvas");
    canvas.width = width + frame * 2;
    canvas.height = height + frame * 2;
    const ctx = canvas.getContext("2d")!;
    roundRectPath(ctx, 0, 0, canvas.width, canvas.height, SKETCH_CARD_RADIUS_PX);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.save();
    roundRectPath(ctx, frame, frame, width, height, SKETCH_CARD_RADIUS_PX - frame / 2);
    ctx.clip();
    ctx.drawImage(image, frame, frame, width, height);
    ctx.restore();
    this.applyFace(visual, canvas, canvas.width, canvas.height, 0);
  }

  private moveVisual(annotationId: string): void {
    this.m_engine.requestRender();
    const sceneId = this.m_activeSceneId;
    if (!sceneId) return;
    const annotation = this.m_annotations
      .getAnnotations(sceneId)
      .find((a) => a.id === annotationId);
    const visual = this.m_visuals.get(annotationId);
    if (!annotation || !annotation.placementPosition || !visual) {
      // Became detached / resolved / removed — full rebuild reconciles it.
      this.rebuild();
      return;
    }
    this.updateVisualTransform(visual, annotation);
  }

  /** Snapshot the Unity-space endpoints into world Three positions. */
  private updateVisualTransform(visual: AnnotationVisual, annotation: AnnotationData): void {
    const placement = annotation.placementPosition;
    if (!placement) return;
    unityToThree(placement, visual.markerWorld);
    unityToThree(annotation.attachmentPosition ?? placement, visual.anchorWorld);
    visual.bubbleMesh.position.copy(visual.markerWorld);
    visual.anchorMesh.position.copy(visual.anchorWorld);
  }

  private tagVisual(visual: AnnotationVisual): void {
    if (!Ioc.isRegistered<PickingService>(PICKING_SERVICE)) return;
    // Tag only the bubble so a click on it selects the annotation; the whole
    // root is markUnpickable so the line + dot never intercept picks.
    const picker = Ioc.resolve<PickingService>(PICKING_SERVICE);
    picker.tagSnapshot(visual.bubbleMesh, visual.id);
  }

  private tick(): void {
    if (this.m_disposed || this.m_visuals.size === 0) return;
    const camera = Ioc.isRegistered<OrbitCameraController>(ORBIT_CAMERA)
      ? Ioc.resolve<OrbitCameraController>(ORBIT_CAMERA).camera
      : null;
    const picker = Ioc.isRegistered<PickingService>(PICKING_SERVICE)
      ? Ioc.resolve<PickingService>(PICKING_SERVICE)
      : null;
    if (!camera) return;

    // Express the camera in the STAGE's frame once, and everything below is
    // unchanged. Marker and anchor positions are stage-local (they come from
    // Unity scene data through `unityToThree` and are written as `.position`
    // on children of `m_root`, which hangs off `engine.root`) — so a world
    // camera pose mixed into them is wrong the moment console placement moves
    // the stage: the billboards would turn with the table instead of with the
    // head, and the constant-screen-size distance would be measured across the
    // offset. Identity outside a placed XR session, so nothing changes there.
    // See `runtime/xr/stageSpace.ts`.
    camera.getWorldPosition(SCRATCH_CAM);
    worldToStagePosition(this.m_engine.root, SCRATCH_CAM, SCRATCH_CAM);
    worldToStageQuaternion(this.m_engine.root, camera.quaternion, SCRATCH_CAM_QUAT);
    // Camera up in the stage frame (billboard local +Y ≈ camera up) — the
    // bezier bows toward it, matching Unity's ±Y control-point offset in
    // billboard-local.
    SCRATCH_UP.set(0, 1, 0).applyQuaternion(SCRATCH_CAM_QUAT).normalize();
    // World size of one canvas pixel per metre of distance, for the active anchor.
    const worldPerPxPerMeter =
      (2 * Math.tan((camera.fov * Math.PI) / 360)) / Math.max(1, this.m_engine.canvas.clientHeight);

    for (const visual of this.m_visuals.values()) {
      // Hidden while its inline editor is open (the editor stands in for it).
      visual.bubbleMesh.visible = visual.id !== this.m_editingId;
      // The entity renderer clears all pick tags on scene activation; heal ours.
      if (picker && picker.resolveSnapshotNode(visual.id) !== visual.bubbleMesh) {
        picker.tagSnapshot(visual.bubbleMesh, visual.id);
      }
      // Billboard: screen-aligned (copy the camera pose), like Unity desktop.
      visual.bubbleMesh.quaternion.copy(SCRATCH_CAM_QUAT);
      // Constant on-screen size: scale ∝ distance cancels perspective.
      const distance = SCRATCH_CAM.distanceTo(visual.markerWorld);
      const scale = annotationBubbleScale(distance);
      visual.scale = scale;
      visual.bubbleMesh.scale.setScalar(scale);
      // Lift the bubble so it grows up from the placement point (pivot bottom).
      const lift = visual.heightPx * scale * 0.5;
      visual.bubbleMesh.position.copy(visual.markerWorld).addScaledVector(SCRATCH_UP, lift);
      if (visual.id === this.m_activeAnchorId) {
        const minRadius = ANCHOR_ACTIVE_PX * worldPerPxPerMeter * SCRATCH_CAM.distanceTo(visual.anchorWorld);
        visual.anchorMesh.scale.setScalar(Math.max(1, minRadius / ANCHOR_RADIUS_M));
      }
      this.updateBezier(visual);
    }
  }

  /** Cubic bezier (40 samples) anchor→placement, control points at mid ± up·bow. */
  private updateBezier(visual: AnnotationVisual): void {
    const p0 = visual.anchorWorld;
    // End at the *visible* card bottom, not the texture bottom: the composed
    // texture has `bottomMarginPx` of transparent shadow margin below the card,
    // so lift the endpoint by that (× scale) or the line stops short of the body.
    const p3 = SCRATCH_P3.copy(visual.markerWorld).addScaledVector(
      SCRATCH_UP,
      visual.bottomMarginPx * visual.scale,
    );
    SCRATCH_MID.copy(p0).add(p3).multiplyScalar(0.5);
    const len = p0.distanceTo(p3);
    const bow = clamp(len * BEZIER_BOW_RATIO, BEZIER_BOW_MIN, BEZIER_BOW_MAX);
    SCRATCH_P1.copy(SCRATCH_MID).addScaledVector(SCRATCH_UP, bow);
    SCRATCH_P2.copy(SCRATCH_MID).addScaledVector(SCRATCH_UP, -bow);

    const positions = visual.line.geometry.getAttribute("position") as BufferAttribute;
    const arr = positions.array as Float32Array;
    const steps = BEZIER_SAMPLES - 1;
    for (let i = 0; i < BEZIER_SAMPLES; i++) {
      const t = i / steps;
      cubicBezier(p0, SCRATCH_P1, SCRATCH_P2, p3, t, SCRATCH_POINT);
      arr[i * 3] = SCRATCH_POINT.x;
      arr[i * 3 + 1] = SCRATCH_POINT.y;
      arr[i * 3 + 2] = SCRATCH_POINT.z;
    }
    positions.needsUpdate = true;
    visual.line.geometry.computeBoundingSphere();
  }
}

// -- Constants (calibrated to Unity's look; see the visual extraction) -------

/** On-screen size: world-scale per metre of camera distance (∝ distance keeps
 *  the marker a constant screen size). Calibrated to Unity's ~8% viewport
 *  bubble; tune with the live preview. */
const SIZE_PER_METER = 0.0009;
const MIN_SCALE = SIZE_PER_METER * 0.5;
const MAX_SCALE = SIZE_PER_METER * 60;
/** Sketch pin card: preview drawn at this face width with a white frame. */
const SKETCH_CARD_WIDTH_PX = 240;
const SKETCH_CARD_FRAME_PX = 8;
const SKETCH_CARD_RADIUS_PX = 16;
/** Anchor dot world radius — small + opaque so it reads as a precise pin. */
const ANCHOR_RADIUS_M = 0.004;
/** How close (canvas px) a press must land to an anchor dot to grab it. */
const ANCHOR_HIT_PX = 8;
/** Smallest on-screen radius (px) of an anchor dot that is hovered or dragged. */
const ANCHOR_ACTIVE_PX = 5;
const LINE_OPACITY = 0.45;
const BEZIER_SAMPLES = 40;
const BEZIER_BOW_RATIO = 0.22;
const BEZIER_BOW_MIN = 0.03;
const BEZIER_BOW_MAX = 0.4;

/** A comment marker handle under the pointer — see `AnnotationsRenderer.hitTestHandle`. */
export interface AnnotationHandleHit {
  readonly annotationId: string;
  /** The billboard bubble (moves the marker) or the surface dot (moves the anchor). */
  readonly handle: "bubble" | "anchor";
  /** Canvas px of the handle's own point — the marker for a bubble, the dot for an
   *  anchor — so a drag can keep the offset the press grabbed it at. */
  readonly screenX: number;
  readonly screenY: number;
}

interface AnnotationVisual {
  readonly id: string;
  readonly group: Group;
  readonly bubbleMesh: Mesh<PlaneGeometry, MeshBasicMaterial>;
  readonly bubbleMaterial: MeshBasicMaterial;
  readonly anchorMesh: Mesh<SphereGeometry, MeshBasicMaterial>;
  readonly anchorMaterial: MeshBasicMaterial;
  readonly line: Line;
  readonly lineMaterial: LineBasicMaterial;
  contentKey: string;
  heightPx: number;
  bottomMarginPx: number;
  scale: number;
  readonly markerWorld: Vector3;
  readonly anchorWorld: Vector3;
}

const SCRATCH_CAM = new Vector3();
/** The camera's rotation IN THE STAGE'S FRAME — see the tick's comment. */
const SCRATCH_CAM_QUAT = new Quaternion();
const SCRATCH_UP = new Vector3();
const SCRATCH_MID = new Vector3();
const SCRATCH_P1 = new Vector3();
const SCRATCH_P2 = new Vector3();
const SCRATCH_P3 = new Vector3();
const SCRATCH_POINT = new Vector3();
const SCRATCH_PROJECTED = new Vector3();
const SCRATCH_NDC = new Vector2();
const SCRATCH_RAYCASTER = new Raycaster();
const SCRATCH_HITS: Intersection[] = [];

/**
 * Unity world → Three world, into `out`.
 *
 * Delegates to the canonical map rather than spelling it out — an inline copy
 * here is exactly what let this file drift from the shared one (task 0004).
 */
function unityToThree(v: AnnotationVec3, out: Vector3): Vector3 {
  const [x, y, z] = convertBasisPosition(v);
  return out.set(x, y, z);
}

function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function cubicBezier(
  p0: Vector3,
  p1: Vector3,
  p2: Vector3,
  p3: Vector3,
  t: number,
  out: Vector3,
): Vector3 {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  out.set(
    a * p0.x + b * p1.x + c * p2.x + d * p3.x,
    a * p0.y + b * p1.y + c * p2.y + d * p3.y,
    a * p0.z + b * p1.z + c * p2.z + d * p3.z,
  );
  return out;
}

/**
 * World units per bubble-texture pixel at `distance` metres from the camera —
 * the constant-screen-size scale. Shared with `annotationPlacement.ts`, which
 * must reserve exactly the footprint drawn here.
 */
export function annotationBubbleScale(distance: number): number {
  return clamp(distance * SIZE_PER_METER, MIN_SCALE, MAX_SCALE);
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
